import { Amplify, API as APIAWS, Auth } from 'aws-amplify';
import axiosClient, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { apiErrorsMesages, fillPath, UserException } from 'helpers/api';
import { AmplifyParams, AuthData, Params, UrlParams } from 'types';

import { getConfig } from './utils';

/**
 * <<<<<<<<<<<<<<<<<<<<<<<<<Config>>>>>>>>>>>>>>>>>>>>>>>>>
 */

export const getEndpointV2 = (args?: {
  url?: string;
  path?: string;
  params?: Params;
  urlParams?: UrlParams;
}): string => {
  const { KODS_APP_ENV = 'test' } = process.env;

  const { path = '', params, urlParams, url } = args || {};

  const envPrefix = KODS_APP_ENV === 'prod' ? '' : `${KODS_APP_ENV}.`;

  const flattenPath = fillPath({
    path,
    params,
    urlParams,
  });

  const flattenUrl =
    url &&
    fillPath({
      path: url,
      params,
      urlParams,
    });

  return flattenUrl
    ? flattenUrl
    : `https://${KODS_APP_ENV === 'omega' ? '' : envPrefix}mx.${
        KODS_APP_ENV === 'omega' ? 'marte-' : ''
      }edx.services${flattenPath}`;
};

const config = getConfig();

const amplifyConfig = {
  Auth: {
    mandatorySignIn: true,
    region: config.cognito.REGION,
    userPoolId: config.cognito.USER_POOL_ID,
    identityPoolId: config.cognito.IDENTITY_POOL_ID,
    userPoolWebClientId: config.cognito.APP_CLIENT_ID,
  },
  Storage: {
    region: config.s3.REGION,
    identityPoolId: config.cognito.IDENTITY_POOL_ID,
  },
  API: {
    endpoints: [
      {
        name: 'DUMMY',
        endpoint: '',
        region: config.apiGateway.REGION,
      },
    ],
  },
};

Amplify.configure(amplifyConfig);

const handleError = (e: any) => {
  let message = '<Unknown error message>';

  if (typeof e?.response?.data?.message === 'string') {
    message = e?.response?.data?.message;
  } else if (typeof e?.response?.data === 'string') {
    message = e?.response?.data;
  } else if (typeof e?.message === 'string') {
    message = e?.message;
  }

  if (message === apiErrorsMesages.networkError) {
    message =
      'Problemas en la solicitud. Por favor, intente acotar los parámetros de la búsqueda o verifique su conexión a Internet.';
  }

  throw new UserException({
    message,
  });
};

// no authorized CRUD client;
export const axios = (args: AxiosRequestConfig): AxiosPromise => {
  return axiosClient(args)
    .then((response) => response)
    .catch(handleError);
};

// authorized CRUD client;
export async function amplifyClientNew<Response = unknown>({
  url,
  method = 'get',
  data: body,
  headers: customHeaders = {},
}: AmplifyParams): Promise<Response> {
  const headers = {
    Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
    ...customHeaders,
  };

  return APIAWS[method]('DUMMY', url, { body, headers })
    .then((response) => response)
    .catch(handleError);
}

export async function amplifyClient<Response = unknown>({
  url,
  method = 'get',
  data: body,
  headers: customHeaders = {},
}: AmplifyParams): Promise<Response> {
  const headers = {
    Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
    ...customHeaders,
  };

  return APIAWS[method]('DUMMY', url, { body, headers })
    .then((response) => response)
    .catch(handleError);
}

export const cognito = {
  // AUTH
  signIn: ({ email: username, password }: { email: string; password: string }): Promise<unknown> =>
    Auth.signIn({ username, password }),

  signOut: (): Promise<unknown> => Auth.signOut(),

  completeNewPassword: async ({
    authData,
    password,
    newPassword,
  }: {
    authData: AuthData;
    password: string;
    newPassword: string;
  }): Promise<unknown> => {
    const { email } = authData.challengeParam.userAttributes;
    const user = await Auth.signIn({ username: email, password });
    return Auth.completeNewPassword(user, newPassword);
  },

  changePassword: async ({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }): Promise<unknown> => {
    const user = await Auth.currentAuthenticatedUser();
    return Auth.changePassword(user, oldPassword, newPassword);
  },

  forgotPassword: async ({ username }: { username: string }): Promise<unknown> => {
    return Auth.forgotPassword(username);
  },

  forgotPasswordSubmit: async ({
    username,
    pass,
    code,
  }: {
    username: string;
    code: string;
    pass: string;
  }): Promise<unknown> => {
    return Auth.forgotPasswordSubmit(username, code, pass);
  },
};
